import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Paper,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import "react-toastify/dist/ReactToastify.css";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setAuthInfo } from "@/redux/auth/auth.slice";

import ToastService from "@/service/toaster.service";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import customerAuthService from "@/service/auth.service/customer.auth.service";
import { GoogleLogin } from "@react-oauth/google";

function CustomerLogin() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({ email: "", password: "" });

  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const savedEmail = localStorage.getItem("email");
    const savedPassword = localStorage.getItem("password");
    if (savedEmail && savedPassword) {
      setFormData({ email: savedEmail, password: savedPassword });
      setRememberMe(true);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (value.trim() !== "") {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    }

    // Email format validation
    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          email: "Please enter a valid email address.",
        }));
      } else {
        setErrors((prevErrors) => ({ ...prevErrors, email: "" }));
      }
    }
  };

  const handleRememberMeChange = (e) => {
    setRememberMe(e.target.checked);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Manual required validation
    let hasError = false;
    const newErrors = { email: "", password: "" };

    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
      hasError = true;
    }
    if (!formData.password.trim()) {
      newErrors.password = "Password is required.";
      hasError = true;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address.";
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      const response = await customerAuthService.loginCustomer(
        formData.email.toLowerCase(),
        formData.password
      );

      if (!response.status && response?.data.is_email_verified === false) {
        ToastService.error(response.data.error);
        return;
      }

      if (response.status === false) {
        setError(response.data.error);
        ToastService.error(response.data.error);
      }

      if (response.status === true) {
        localStorage.setItem("access_token", response.data.access_token);
        localStorage.setItem("role", "customer");

        dispatch(
          setAuthInfo({
            ...response.data.data,
            role: "customer",
            id: response.data.data._id,
          })
        );

        if (rememberMe) {
          localStorage.setItem("email", formData.email);
          localStorage.setItem("password", formData.password);
        } else {
          localStorage.removeItem("email");
          localStorage.removeItem("password");
        }

        setTimeout(() => {
          // navigate("/customer/dashboard");
          navigate("/customer/search-brokers");
        }, 1000);
      }
    } catch (error) {
      if (error?.data?.data?.is_email_verified === false) {
        ToastService.error("Please verify your email address to log in.");
        setTimeout(() => {
          navigate("/customer/auth/verify-email", {
            state: { email: formData.email },
          });
        }, 1000);
      } else {
        const message =
          error?.data?.data?.error ||
          error?.response?.data?.message ||
          "Login failed!";
        ToastService.error(message);
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" flexDirection={isMobile ? "column" : "row"}>
      <Paper
        sx={{
          width: isMobile ? "100%" : isTablet ? "75%" : 450,
          p: "102px 55px",
          textAlign: "center",
          borderRadius: 2,
          border: "2px solid #CDCCF0",
        }}
        elevation={5}
      >
        <Typography variant="h4" color="#5E5ACF" fontWeight="bold">
          Log-in
        </Typography>
        <form onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            margin="normal"
            label="Email"
            variant="outlined"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={Boolean(errors.email)}
            helperText={errors.email}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Password"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            name="password"
            value={formData.password}
            onChange={handleChange}
            error={Boolean(errors.password)}
            helperText={errors.password}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={rememberMe}
                  onChange={handleRememberMeChange}
                />
              }
              label="Remember me"
            />
            <Typography variant="body2" color="primary" sx={{ cursor: "pointer" }}>
              <Link to="/customer/auth/forgot-password" style={{ textDecoration: "none" }}>
                Forgot password?
              </Link>
            </Typography>
          </Box>
          {error && (
            <Typography color="error" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              mt: 2,
              background: "#F4B400",
              color: "black",
              fontWeight: "bold",
              "&:hover": { background: "#F4B400" },
            }}
            disabled={loading}
          >
            {loading ? "Logging in..." : "Log in"}
          </Button>
        </form>
        <Box mt={2}>
  <GoogleLogin
    onSuccess={async (credentialResponse) => {
      try {
        const idToken = credentialResponse.credential;
        if (!idToken) {
          ToastService.error("Google login failed");
          return;
        }

        // Send token to your backend
        const response = await customerAuthService.googleLogin(idToken);

        if (response.status) {
          localStorage.setItem("access_token", response.data.access_token);
          localStorage.setItem("role", "customer");

          dispatch(
            setAuthInfo({
              ...response.data.data,
              role: "customer",
              id: response.data.data._id,
            })
          );

          ToastService.success("Logged in with Google!");

          setTimeout(() => {
            navigate("/customer/search-brokers");
          }, 1000);
        } else {
          ToastService.error(response.data.error || "Login failed!");
        }
      } catch (err) {
        console.error("Google login error:", err);
        ToastService.error("Google login error!");
      }
    }}
    onError={() => {
      ToastService.error("Google login failed!");
    }}
  />
</Box>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Don't have an account?{" "}
          <Link
            to="/customer/auth/register"
            style={{
              color: "#6a00f4",
              fontWeight: "bold",
              textDecoration: "none",
            }}
          >
            Sign up
          </Link>
        </Typography>
      </Paper>
    </Box>
  );
}

export default CustomerLogin;
